import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Icon,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Link as NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Icons from "../../assets/js/Icons";
import { handleGetMySubscription } from "../../redux/member-slices/payment.slice";
import { setSubscription } from "../../redux/user-slices/subscription.slice";
import moment from "moment";
import { checkToken } from "utils/helper";

const SubscriptionThankYou = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const authChecked = checkToken();

  const { plan, subscription } = useSelector(
    (state) => state.subscriptionSlice
  );

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      setLoading(true);
      try {
        const res = await dispatch(handleGetMySubscription()).unwrap();
        if (res) {
          dispatch(setSubscription(res));
        }
      } catch (err) {
        console.error("Error fetching subscription after purchase:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [dispatch]);

  const planName = plan?.name || subscription?.planSnapshot?.name || "Subscription";
  const billingCycle = plan?.billingCycle || subscription?.planSnapshot?.billingCycle || "Monthly";

  const getPlanFeatures = (name) => {
    switch (name?.toLowerCase()) {
      case "platinum":
        return [
          "Full access to read blogs & view/comment on community forums",
          "Find and view detailed profiles of all specialists",
          "Chat access with unlimited specialists",
          "3 free webinars included",
          "Unlimited appointment bookings",
        ];
      case "premium":
        return [
          "Full access to read blogs & view/comment on community forums",
          "Find and view detailed profiles of all specialists",
          "Chat access with up to 3 specialists per month",
          "1 free webinar included",
          "Unlimited appointment bookings",
        ];
      default:
        return [
          "Full access to read blogs & view/comment on community forums",
          "Find specialists and access member features",
          "Unlimited appointment bookings",
        ];
    }
  };

  const features = getPlanFeatures(planName);

  return (
    <Box sx={{ py: 6, minHeight: "75vh", bgcolor: "#f8fafc" }}>
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            bgcolor: "#ffffff",
          }}
        >
          {/* Header Icon & Title */}
          <Stack spacing={2} alignItems="center" textAlign="center" mb={4}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#e6f4ea",
                color: "#1e8e3e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
                boxShadow: "0 4px 14px rgba(30, 142, 62, 0.15)",
              }}
            >
              <Icon sx={{ fontSize: 44, display: "flex", overflow: "visible" }}>
                <Icons.Checkmark />
              </Icon>
            </Box>

            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              color="primary.main"
              sx={{ fontSize: { xs: 26, sm: 34 } }}
            >
              Thank You for Your Subscription!
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              maxWidth={560}
              fontSize={{ xs: 15, sm: 17 }}
            >
              Your payment has been successfully processed. Your plan is now
              active, giving you full access to InnerPal’s premium features.
            </Typography>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Subscription Summary Card */}
          {loading ? (
            <Stack spacing={2} my={3}>
              <Skeleton variant="rounded" height={60} />
              <Skeleton variant="rounded" height={60} />
            </Stack>
          ) : (
            <Box
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                bgcolor: "#f1f5f9",
                border: "1px solid #cbd5e1",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color="other.color1"
                mb={2}
              >
                Subscription Summary
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <SummaryItem
                  label="Plan Name"
                  value={planName.charAt(0).toUpperCase() + planName.slice(1)}
                />
                <SummaryItem
                  label="Billing Cycle"
                  value={billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}
                />
                <SummaryItem
                  label="Status"
                  value={
                    <Box
                      component="span"
                      sx={{
                        px: 1.5,
                        py: 0.3,
                        borderRadius: "12px",
                        bgcolor: "#dcfce7",
                        color: "#15803d",
                        fontWeight: 600,
                        fontSize: 13,
                        display: "inline-block",
                      }}
                    >
                      Active
                    </Box>
                  }
                />
                <SummaryItem
                  label="Activation Date"
                  value={
                    subscription?.startDate
                      ? moment(subscription.startDate).format("DD MMM YYYY")
                      : moment().format("DD MMM YYYY")
                  }
                />
              </Box>
            </Box>
          )}

          {/* Unlocked Benefits */}
          <Box mb={4}>
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              mb={2}
            >
              What's Included in Your Plan:
            </Typography>

            <Stack spacing={1.5}>
              {features.map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Icon
                    sx={{
                      color: "success.main",
                      fontSize: 22,
                      display: "flex",
                    }}
                  >
                    <Icons.Checkmark />
                  </Icon>
                  <Typography fontSize={15} color="text.primary">
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            pt={1}
          >
            {authChecked && (
              <Button
                variant="contained"
                size="large"
                component={NavLink}
                to="/member/profile/subscription"
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: "30px",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                View My Subscription
              </Button>
            )}

            <Button
              variant="outlined"
              size="large"
              component={NavLink}
              to="/matching-specialist"
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: "30px",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Find a Specialist
            </Button>

            <Button
              variant="text"
              size="large"
              component={NavLink}
              to="/home"
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: "30px",
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              Back to Home
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

const SummaryItem = ({ label, value }) => (
  <Box>
    <Typography fontSize={13} color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Typography fontSize={16} fontWeight={600} color="text.primary" mt={0.3}>
      {value}
    </Typography>
  </Box>
);

export default SubscriptionThankYou;
